import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { TID } from "../../lib/testIds";
import { SOLUTIONS, INDUSTRIES } from "../../data/pages";

const SOLUTION_LINKS = Object.values(SOLUTIONS).map((s) => ({
  label: s.eyebrow,
  to: `/solutions/${s.slug}`,
}));
const INDUSTRY_LINKS = Object.values(INDUSTRIES).map((i) => ({
  label: i.eyebrow,
  to: `/industries/${i.slug}`,
}));

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [dropdown, setDropdown] = useState(null); // "solutions" | "industries" | null
  const [mobileSub, setMobileSub] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setDropdown(null);
    setMobileSub(null);
  }, [location.pathname]);

  const handleContactClick = () => {
    if (isHome) {
      const el = document.getElementById("contact");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/#contact");
      setTimeout(() => {
        const el = document.getElementById("contact");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  };

  return (
    <header
      data-testid={TID.header}
      className={`sticky top-0 z-40 w-full transition-all duration-300 ${
        scrolled || !isHome ? "backdrop-blur-xl bg-[#050505]/85 border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid={TID.headerLogo}
          className="font-headings text-xl md:text-2xl font-black tracking-tighter text-white flex items-center gap-2"
        >
          <span className="w-2 h-2 bg-[#FF5F1F] rounded-full animate-accent-pulse" />
          Techovarya
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {/* Solutions dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setDropdown("solutions")}
            onMouseLeave={() => setDropdown(null)}
          >
            <button
              data-testid={TID.headerNavLink("solutions")}
              className="text-sm text-zinc-300 hover:text-white transition-colors inline-flex items-center gap-1"
            >
              Solutions
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${dropdown === "solutions" ? "rotate-180" : ""}`} />
            </button>
            {dropdown === "solutions" && (
              <div className="absolute top-full left-0 pt-3">
                <div className="w-72 bg-[#0A0A0A] border border-white/10 backdrop-blur-xl shadow-2xl">
                  {SOLUTION_LINKS.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      data-testid={`nav-solution-${l.to.split("/").pop()}`}
                      className="block px-5 py-3.5 text-sm text-zinc-300 hover:bg-[#FF5F1F] hover:text-black transition-colors border-b border-white/5 last:border-b-0"
                    >
                      {l.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Industries dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setDropdown("industries")}
            onMouseLeave={() => setDropdown(null)}
          >
            <button
              data-testid={TID.headerNavLink("industries")}
              className="text-sm text-zinc-300 hover:text-white transition-colors inline-flex items-center gap-1"
            >
              Industries
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${dropdown === "industries" ? "rotate-180" : ""}`} />
            </button>
            {dropdown === "industries" && (
              <div className="absolute top-full left-0 pt-3">
                <div className="w-72 bg-[#0A0A0A] border border-white/10 backdrop-blur-xl shadow-2xl">
                  {INDUSTRY_LINKS.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      data-testid={`nav-industry-${l.to.split("/").pop()}`}
                      className="block px-5 py-3.5 text-sm text-zinc-300 hover:bg-[#FF5F1F] hover:text-black transition-colors border-b border-white/5 last:border-b-0"
                    >
                      {l.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Link
            to="/work"
            data-testid={TID.headerNavLink("work")}
            className="text-sm text-zinc-300 hover:text-white transition-colors"
          >
            Work
          </Link>
          <Link
            to="/about"
            data-testid={TID.headerNavLink("about")}
            className="text-sm text-zinc-300 hover:text-white transition-colors"
          >
            About
          </Link>
          <button
            onClick={handleContactClick}
            data-testid={TID.headerNavLink("contact")}
            className="text-sm text-zinc-300 hover:text-white transition-colors"
          >
            Contact
          </button>
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid={TID.headerCta}
            onClick={handleContactClick}
            className="hidden md:inline-flex items-center gap-2 px-4 py-2.5 border border-white/15 bg-white/[0.02] hover:bg-white/[0.06] text-white text-sm font-medium transition-colors"
          >
            Book a Strategy Call
          </button>
          <button
            data-testid={TID.mobileMenuToggle}
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-white p-2 border border-white/10"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid={TID.mobileMenu}
          className="lg:hidden bg-[#050505]/95 backdrop-blur-xl border-t border-white/10 px-6 py-6 flex flex-col gap-2 max-h-[85vh] overflow-y-auto"
        >
          {/* Solutions */}
          <button
            onClick={() => setMobileSub(mobileSub === "solutions" ? null : "solutions")}
            className="flex items-center justify-between py-3 text-base font-medium text-white border-b border-white/5"
          >
            Solutions
            <ChevronDown className={`w-4 h-4 transition-transform ${mobileSub === "solutions" ? "rotate-180" : ""}`} />
          </button>
          {mobileSub === "solutions" &&
            SOLUTION_LINKS.map((l) => (
              <Link key={l.to} to={l.to} className="pl-4 py-2 text-sm text-zinc-400 hover:text-[#FF5F1F]">
                {l.label}
              </Link>
            ))}

          {/* Industries */}
          <button
            onClick={() => setMobileSub(mobileSub === "industries" ? null : "industries")}
            className="flex items-center justify-between py-3 text-base font-medium text-white border-b border-white/5"
          >
            Industries
            <ChevronDown className={`w-4 h-4 transition-transform ${mobileSub === "industries" ? "rotate-180" : ""}`} />
          </button>
          {mobileSub === "industries" &&
            INDUSTRY_LINKS.map((l) => (
              <Link key={l.to} to={l.to} className="pl-4 py-2 text-sm text-zinc-400 hover:text-[#FF5F1F]">
                {l.label}
              </Link>
            ))}

          <Link to="/work" className="py-3 text-base font-medium text-white border-b border-white/5">
            Work
          </Link>
          <Link to="/about" className="py-3 text-base font-medium text-white border-b border-white/5">
            About
          </Link>
          <button
            onClick={handleContactClick}
            className="py-3 text-left text-base font-medium text-white border-b border-white/5"
          >
            Contact
          </button>
          <button
            onClick={handleContactClick}
            className="mt-4 bg-[#FF5F1F] text-black font-bold py-3 text-sm"
          >
            Book a Strategy Call
          </button>
        </div>
      )}
    </header>
  );
};
